/**
 * Wire-level types for the Notification Service. Shapes mirror the published
 * API contract so swapping mocks for live calls is structural-zero-change.
 */

import { maskEmailsInText } from '~/utils/masking';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type ChannelType = 'email' | 'slack' | 'in_app';
export type Severity = 'low' | 'medium' | 'high' | 'critical';
export type EntityStatus = 'ACTIVE' | 'DISABLED' | 'DEPRECATED' | 'BETA';

// ---------------------------------------------------------------------------
// Common envelopes
// ---------------------------------------------------------------------------

/** Standard envelope: every response is wrapped with `{ success?, message?, data }`. */
export interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data: T;
}

/** Paginated envelope used by list endpoints (inbox, notifications/list). */
export interface ApiPaginated<T> {
  data: T[];
  pagination: { total: number; page: number; size: number; pages: number };
}

// ---------------------------------------------------------------------------
// Configurations (alert topics: "Authentication Expired", "Feed Ingestion Failure", …)
// ---------------------------------------------------------------------------

/** Who a notification configuration is intended for. */
export type Audience = 'customer' | 'internal';

/** A single notification configuration (alert topic) returned by the admin endpoint. */
export interface ConfigurationResponse {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category_group: string;
  audience: Audience;
  default_severity: Severity;
  default_channels: ChannelType[];
  status: EntityStatus;
  version: number;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Preferences (per-configuration × per-channel toggle)
// ---------------------------------------------------------------------------

/** Channel-shaped target — varies by channel_type. */
export interface PreferenceTargetEmail {
  to: string[];
  cc?: string[];
  bcc?: string[];
}
export interface PreferenceTargetSlack {
  channel_id: string;
}
export interface PreferenceTargetInApp {
  audience: string;
  /** Auto-mark as read after N days. `null` = never. Matches backend `InAppTarget.auto_read_after_days`. */
  auto_read_after_days?: number | null;
}
export type PreferenceTarget =
  | PreferenceTargetEmail
  | PreferenceTargetSlack
  | PreferenceTargetInApp;

/** A single preference row returned by GET /preferences. */
export interface PreferenceResponse {
  id: string;
  subsidiary_id: string;
  configuration_id: string;
  channel_type: ChannelType;
  target: PreferenceTarget;
  status: EntityStatus;
  min_severity: Severity;
  version: number;
  created_at: string;
  updated_at: string;
}

/** Body for `PUT /preferences` — upserts by (configuration_id, channel_type). */
export interface UpsertPreferencePayload {
  configuration_id: string;
  channel_type: ChannelType;
  target: PreferenceTarget;
  status?: EntityStatus;
  min_severity?: Severity;
}

/**
 * Body for `PUT /preferences/bulk` — applies one channel target+status to
 * multiple configuration IDs in a single round-trip.
 */
export interface BulkUpsertPreferencesPayload {
  channel_type: ChannelType;
  configuration_ids: string[];
  target: PreferenceTarget;
  status?: EntityStatus;
  min_severity?: Severity;
}

// ---------------------------------------------------------------------------
// Channel configs (admin)
// ---------------------------------------------------------------------------

/** Channel-config row. The `credentials` field is intentionally never returned. */
export interface ChannelConfigResponse {
  id: string;
  channel_type: ChannelType;
  provider: string;
  status: EntityStatus;
  config: Record<string, unknown>;
  version: number;
  created_at: string;
  updated_at: string;
}

/** Body for `PUT /admin/channel-config/{channel_type}`. */
export interface UpsertChannelConfigPayload {
  channel_type: ChannelType;
  provider: string;
  status?: EntityStatus;
  config?: Record<string, unknown>;
  credentials: Record<string, unknown>;
}

/**
 * In-app test card. Backend supports more fields than this; we only send the
 * subset the Send Test button needs (title + description). Extend as needed
 * when more in-app card surfaces are wired.
 */
export interface InAppTestCard {
  title: string;
  description: string;
}

/** Body for `POST /admin/channel-config/{channel_type}/test`. */
export interface ChannelTestPayload {
  to?: string[];
  subject?: string;
  html_body?: string;
  text_body?: string;
  channel_id?: string;
  slack_text?: string;
  in_app_card?: InAppTestCard;
}

/** Result returned by `POST .../test`. */
export interface ChannelTestResult {
  ok: boolean;
  provider_message_id: string | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build a stable preferences map keyed by `${configuration_id}:${channel_type}`. */
export type PreferenceKey = `${string}:${ChannelType}`;
export function preferenceKey(configurationId: string, channelType: ChannelType): PreferenceKey {
  return `${configurationId}:${channelType}`;
}

/**
 * In-app channel is "enabled" for the subsidiary when at least one `in_app`
 * preference row is ACTIVE. Single source of truth shared by the settings form
 * (initial toggle state) and the SSE provider (whether to open the stream).
 */
export function isInAppEnabled(preferences: PreferenceResponse[]): boolean {
  return preferences.some((p) => p.channel_type === 'in_app' && p.status === 'ACTIVE');
}

/** Email channel is "enabled" when at least one `email` preference row is ACTIVE. */
export function isEmailEnabled(preferences: PreferenceResponse[]): boolean {
  return preferences.some((p) => p.channel_type === 'email' && p.status === 'ACTIVE');
}

// ---------------------------------------------------------------------------
// Notifications list (POST /notifications/list) — raw dispatch entries
// ---------------------------------------------------------------------------

/** Raw event payload embedded in each dispatch entry. */
export interface BackendNotificationPayload {
  use_case?: string;
  error_message?: string;
  connector_code?: string;
  workspace_name?: string;
  workflow_run_id?: string;
  stage?: string;
  stage_status?: string;
  total_products?: number;
  duration_seconds?: number;
  [key: string]: unknown;
}

/** A single entry returned by POST /notifications/list. */
export interface BackendNotificationEntry {
  id: string;
  subsidiary_id: string;
  configuration_id: string;
  source_system: string;
  ingest_method: string;
  correlation_id: string | null;
  severity: Severity;
  payload: BackendNotificationPayload;
  status: 'completed' | 'partial' | 'dispatching' | 'failed';
  error_message: string | null;
  total_targets: number;
  sent_count: number;
  failed_count: number;
  received_at: string;
  completed_at: string | null;
  created_at: string;
}

/** Full paginated response from POST /notifications/list. */
export interface BackendNotificationListResponse {
  success: boolean;
  message: string;
  data: BackendNotificationEntry[];
  pagination: {
    total: number;
    page: number;
    size: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

/** Normalises a POST /notifications/list entry to the frontend InboxNotification shape. */
export function toInboxNotificationFromEntry(entry: BackendNotificationEntry): InboxNotification {
  const p = entry.payload;

  const title = p.use_case ?? 'Notification';

  let description: string | null = null;
  if (p.error_message) {
    description = p.error_message;
  } else if (p.stage && p.stage_status) {
    const productSuffix =
      p.total_products !== undefined ? ` — ${p.total_products.toLocaleString()} products` : '';
    description = `${p.stage.charAt(0).toUpperCase()}${p.stage.slice(1)} stage ${p.stage_status}${productSuffix}`;
  } else if (p.total_products !== undefined && p.duration_seconds !== undefined) {
    description = `${p.total_products.toLocaleString()} products processed in ${String(p.duration_seconds)}s`;
  } else if (p.total_products !== undefined) {
    description = `${p.total_products.toLocaleString()} products`;
  }

  const context = p.connector_code ?? p.workspace_name ?? '';

  const tags: NotificationTag[] = [];
  if (entry.severity === 'critical') {
    tags.push('Critical');
  } else if (entry.severity === 'high' || entry.status === 'partial') {
    tags.push('Warning');
  } else {
    tags.push('Info');
  }

  return {
    id: entry.id,
    title,
    description,
    severity: entry.severity,
    type: '',
    context,
    tags,
    is_read: false,
    created_at: entry.received_at,
    configuration_id: entry.configuration_id,
    has_action: false,
  };
}

// ---------------------------------------------------------------------------
// Inbox (in-app notification feed)
// ---------------------------------------------------------------------------

/** Visual badge labels rendered on inbox notification cards. */
export type NotificationTag =
  | 'Critical'
  | 'Warning'
  | 'Action Needed'
  | 'Info'
  | 'NEW'
  | 'Applied'
  | 'Validated';

/**
 * Entity reference attached to a notification for future type-safe navigation.
 * The `type` drives which route getNotificationRoute() resolves to.
 */
export interface InboxNotificationEntityRef {
  type: 'experiment' | 'workflow' | 'catalog' | 'calibration' | 'feed';
  id: string;
  /** Human-readable label shown in the notification context slot. */
  label?: string;
}

/** Single notification item returned by GET /notifications/inbox. */
export interface InboxNotification {
  id: string;
  title: string;
  description: string | null;
  severity: Severity;
  /** Dot-namespaced type code (e.g. "feed.ingestion_failed") — drives navigation. */
  type: string;
  /** Short context string rendered on the right of the notification (e.g. "Workflow 3"). */
  context: string;
  /** Badge labels rendered on the card (e.g. ["Critical", "NEW"]). */
  tags: NotificationTag[];
  /** Backend-supplied lucide icon name (kebab-case, e.g. "rocket") for the card
   *  glyph. Absent/unknown names fall back to the severity icon. */
  icon?: string;
  is_read: boolean;
  created_at: string;
  configuration_id?: string;
  entity_ref?: InboxNotificationEntityRef;
  /** When true, a "View More" CTA renders below the description. */
  has_action?: boolean;
}

/** Paginated inbox response envelope — frontend-facing shape used by the UI. */
export interface InboxResponse {
  notifications: InboxNotification[];
  unread_count: number;
  pagination: { total: number; page: number; size: number; pages: number };
}

// ---------------------------------------------------------------------------
// Backend wire types for POST /notifications/inbox/list
// ---------------------------------------------------------------------------

/**
 * Rendered in-app card object returned inside each InboxEntry.
 * Core fields (title, body, action_url) are always present when the template
 * renders; extended fields (type, context, tags, entity_ref, has_action) are
 * populated by templates that include them.
 */
export interface BackendInboxCard {
  title?: string;
  body?: string;
  icon?: string;
  action_url?: string;
  type?: string;
  context?: string;
  tags?: NotificationTag[];
  entity_ref?: InboxNotificationEntityRef;
  has_action?: boolean;
}

/** A single inbox entry returned by POST /notifications/inbox/list. */
export interface BackendInboxEntry {
  notification_id: string;
  configuration_id: string;
  subsidiary_id: string;
  severity: Severity;
  received_at: string | null;
  card: BackendInboxCard | null;
  read_at: string | null;
  dismissed_at: string | null;
}

/** Full paginated response from POST /notifications/inbox/list. */
export interface BackendInboxListResponse {
  success: boolean;
  message: string;
  data: BackendInboxEntry[];
  pagination: {
    total: number;
    page: number;
    size: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

/** Response envelope from GET /notifications/inbox/unread-count. */
export interface BackendUnreadCountResponse {
  success: boolean;
  message: string;
  data: { count: number };
}

/** Normalises a backend InboxEntry to the frontend InboxNotification shape. */
export function toInboxNotification(entry: BackendInboxEntry): InboxNotification {
  const card = entry.card ?? {};
  // Mask any email addresses in the notification body to prevent PII exposure
  // in the shared inbox surface (e.g. membership-invite bodies include the
  // invitee's full email, visible to all subsidiary members — AGENT-187).
  const description = card.body != null ? maskEmailsInText(card.body) : null;
  return {
    id: entry.notification_id,
    title: card.title ?? '',
    description,
    severity: entry.severity,
    type: card.type ?? '',
    context: card.context ?? '',
    tags: card.tags ?? [],
    ...(card.icon !== undefined && { icon: card.icon }),
    is_read: entry.read_at !== null,
    created_at: entry.received_at ?? new Date().toISOString(),
    configuration_id: entry.configuration_id,
    ...(card.entity_ref !== undefined && { entity_ref: card.entity_ref }),
    has_action: card.has_action ?? !!card.action_url,
  };
}
